import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";

import LazyPropertyImage from "../../../components/LazyPropertyImage";
import { IRentedPropertyPhoto } from "../types/ownerDashboard.types";

interface RentedPropertyPhotoCarouselProps {
  photos: IRentedPropertyPhoto[];
  title: string;
}

function getPhotoUrl(photo: IRentedPropertyPhoto): string {
  return photo.url_l || photo.url_m || photo.url_s || "";
}

export default function RentedPropertyPhotoCarousel({
  photos,
  title,
}: RentedPropertyPhotoCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullscreenUrl, setFullscreenUrl] = useState<string | null>(null);
  const validPhotos = useMemo(
    () => photos.filter((photo) => getPhotoUrl(photo)),
    [photos]
  );

  if (validPhotos.length === 0) {
    return null;
  }

  const activePhoto = validPhotos[activeIndex];
  const activePhotoUrl = getPhotoUrl(activePhoto);
  const hasMultiplePhotos = validPhotos.length > 1;

  const goToPrevious = () => {
    setActiveIndex((current) =>
      current === 0 ? validPhotos.length - 1 : current - 1
    );
  };

  const goToNext = () => {
    setActiveIndex((current) =>
      current === validPhotos.length - 1 ? 0 : current + 1
    );
  };

  return (
    <>
      <div className="relative mt-4 overflow-hidden rounded-xl border border-white/[0.08]">
        <div className="relative h-56 w-full bg-[#0d1117] sm:h-72">
          <LazyPropertyImage
            src={activePhotoUrl}
            alt={`${title} - Foto ${activeIndex + 1}`}
            loading="eager"
            className="h-full w-full object-cover"
          />

          <button
            type="button"
            onClick={() => setFullscreenUrl(activePhotoUrl)}
            className="absolute bottom-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-[#0d1117]/80 text-[#f0f6fc] transition-colors hover:border-[#6ea8ff]/50 hover:text-[#6ea8ff]"
            aria-label="Ver foto em tamanho maior"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>

        {hasMultiplePhotos && (
          <>
            <button
              type="button"
              onClick={goToPrevious}
              className="absolute left-3 top-1/2 z-[1] flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-[#0d1117]/85 text-[#f0f6fc] transition-colors hover:border-[#6ea8ff]/45 hover:text-[#6ea8ff]"
              aria-label="Foto anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={goToNext}
              className="absolute right-3 top-1/2 z-[1] flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-[#0d1117]/85 text-[#f0f6fc] transition-colors hover:border-[#6ea8ff]/45 hover:text-[#6ea8ff]"
              aria-label="Próxima foto"
            >
              <ChevronRight className="h-4 w-4" />
            </button>

            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
              {validPhotos.map((photo, index) => (
                <button
                  key={photo.id}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`h-1.5 w-1.5 rounded-full transition-colors ${
                    index === activeIndex ? "bg-[#6ea8ff]" : "bg-[#6e7681]"
                  }`}
                  aria-label={`Ir para foto ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {fullscreenUrl && (
        <div
          className="fixed inset-0 z-[1300] flex items-center justify-center bg-[#0d1117]/95 p-4"
          role="presentation"
          onClick={() => setFullscreenUrl(null)}
        >
          <button
            type="button"
            onClick={() => setFullscreenUrl(null)}
            className="absolute right-4 top-4 rounded-full p-2 text-[#f0f6fc] transition-colors hover:bg-white/10"
            aria-label="Fechar foto ampliada"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={fullscreenUrl}
            alt={`${title} - Foto ampliada`}
            className="max-h-[90vh] max-w-full rounded-lg object-contain"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
